import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/api';
import { FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import './Auth.css';
import PasswordRequirements from '../../components/PasswordRequirements/PasswordRequirements';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setError('Please fill in both fields.');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await authService.resetPassword(token, newPassword);
      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError('Invalid or expired reset link. Please request a new one.');
      console.error('Reset password error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <div className="auth-logo">
              <Logo className="auth-logo-svg" />
            </div>
            <h1>Invalid Link</h1>
          </div>
          <div className="auth-success-message">
            <div className="success-icon">⚠️</div>
            <p>This password reset link is invalid. Please request a new one.</p>
            <Link to="/forgot-password" className="btn btn-primary btn-block">
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">
            <Logo className="auth-logo-svg" />
          </div>
          <h1>Set New Password</h1>
          <p>Enter your new password below</p>
        </div>

        {success ? (
          <div className="auth-success-message">
            <div className="success-icon">✅</div>
            <h3>Password Reset!</h3>
            <p>Your password has been successfully reset. You can now sign in with your new password.</p>
            <Link to="/login" className="btn btn-primary btn-block">
              Sign In
            </Link>
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            {error && <div className="auth-error">{error}</div>}

            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">New Password</label>
              <div className="input-with-icon">
                <FiLock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="newPassword"
                  className="form-input"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                  autoFocus
                />
                <button
                  type="button"
                  className="input-action"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <PasswordRequirements password={newPassword} />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
              <div className="input-with-icon">
                <FiLock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  className="form-input"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block btn-lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner spinner-sm"></span>
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}

        <div className="auth-switch">
          <Link to="/login" className="auth-link">
            Back to Sign In
          </Link>
        </div>
      </div>

      <div className="auth-footer">
        <p>The Pearson Clan &bull; Family Network</p>
      </div>
    </div>
  );
};

export default ResetPassword;